import { QueryClient } from '@tanstack/react-query';
import { APP_CONFIG } from './app';

/**
 * Creates a configured QueryClient instance for AI DTR
 * 
 * Optimized for:
 * - Document analysis workflows with appropriate caching
 * - Error handling for network failures
 * - Retry logic that respects HTTP status codes
 * - Development vs production behavior
 */
export const createQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: APP_CONFIG.queryClient.staleTime,
        cacheTime: APP_CONFIG.queryClient.cacheTime,
        retry: (failureCount, error: any) => {
          // Don't retry on 4xx errors (client errors)
          if (error?.response?.status >= 400 && error?.response?.status < 500) {
            return false;
          }
          // Don't retry on network errors in development (faster feedback)
          if (APP_CONFIG.isDevelopment && error?.code === 'NETWORK_ERROR') {
            return false;
          }
          // Retry up to configured attempts for other errors
          return failureCount < APP_CONFIG.queryClient.retryAttempts;
        },
        retryDelay: (attemptIndex) => {
          // Exponential backoff with max delay of 30 seconds
          return Math.min(1000 * 2 ** attemptIndex, 30000);
        },
        refetchOnWindowFocus: false, // Prevent unnecessary refetches during document work
        refetchOnReconnect: true, // Refetch when connection is restored
        refetchOnMount: true, // Always refetch on component mount for fresh data
      },
      mutations: {
        retry: 1, // Retry mutations once on failure
        onError: (error) => {
          console.error('Mutation error:', error);
          
          // In production, send to error reporting service
          if (APP_CONFIG.features.enableErrorReporting) {
            // TODO: Integrate with error reporting service
            // errorReportingService.captureException(error);
          }
          
          // In development, provide more detailed logging
          if (APP_CONFIG.isDevelopment) {
            console.group('Mutation Error Details');
            console.error('Error object:', error);
            console.error('Stack trace:', (error as Error)?.stack);
            console.groupEnd();
          }
        },
      },
    },
  });
};

export default createQueryClient;
